import { useState } from 'react';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Star, Pencil, Trash2, FileText, Search, MapPin, Users, Tag } from 'lucide-react';
import { TemplateFormSheet } from './template-form-sheet';
import type { ProjectTemplate as FormProjectTemplate, CreateTemplateData } from '@/types/template.types';
import { useTemplates, type ProjectTemplate } from '@/contexts/TemplateContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Helper to convert TemplateContext's ProjectTemplate to the form's ProjectTemplate type
const toFormTemplate = (template: ProjectTemplate): FormProjectTemplate => ({
  id: template.id,
  templateName: template.name,
  templateDescription: template.templateDescription,
  isFavorite: template.isFavorite,
  createdAt: template.createdAt.toISOString(),
  updatedAt: template.updatedAt.toISOString(),
  description: template.description,
  priority: template.priority,
  type: template.type,
  location: template.location,
  unit: template.unit as FormProjectTemplate['unit'],
  currencyCode: template.currencyCode,
  clients: template.clients,
  tags: template.tags,
});

interface ManageTemplatesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageTemplatesSheet({ open, onOpenChange }: ManageTemplatesSheetProps) {
  const { preferences } = useUserPreferences();
  const { templates, addTemplate, updateTemplate, deleteTemplate, toggleFavorite } = useTemplates();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [formSheetOpen, setFormSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ProjectTemplate | null>(null);

  // Determine which side to open from based on sidebar position
  const sheetSide = preferences.sidebarPosition === 'right' ? 'left' : 'right';

  // Filter templates based on search
  const filteredTemplates = templates.filter(
    (template) =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.templateDescription?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setSheetMode('create');
    setFormSheetOpen(true);
  };

  const handleEditTemplate = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setSheetMode('edit');
    setFormSheetOpen(true);
  };

  const handleDeleteClick = (template: ProjectTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
      toast.success('Template deleted successfully');
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveTemplate = (data: CreateTemplateData) => {
    if (sheetMode === 'create') {
      // Create new template
      addTemplate({
        name: data.templateName,
        templateDescription: data.templateDescription,
        description: data.description,
        priority: data.priority,
        type: data.type,
        location: data.location,
        unit: data.unit,
        currencyCode: data.currencyCode,
        clients: data.clients,
        tags: data.tags,
        isFavorite: false,
      });
      toast.success('Template created successfully');
    } else if (selectedTemplate) {
      // Update existing template
      updateTemplate(selectedTemplate.id, {
        name: data.templateName,
        templateDescription: data.templateDescription,
        description: data.description,
        priority: data.priority,
        type: data.type,
        location: data.location,
        unit: data.unit,
        currencyCode: data.currencyCode,
        clients: data.clients,
        tags: data.tags,
      });
      toast.success('Template updated successfully');
    }
  };

  const handleToggleFavorite = (templateId: string) => {
    toggleFavorite(templateId);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side={sheetSide} className="w-full sm:max-w-xl p-0 flex flex-col h-full">
          {/* Fixed Header */}
          <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
            <SheetHeader>
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>Manage Templates</SheetTitle>
                  <SheetDescription>
                    Create, edit, and organize your project templates.
                  </SheetDescription>
                </div>
                <Button size="sm" onClick={handleCreateTemplate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create
                </Button>
              </div>
            </SheetHeader>

            {/* Search Bar */}
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport className="h-full max-h-full">
              <div className="px-6 py-4">
            {filteredTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchTerm ? 'No templates found' : 'No templates yet'}
                </h3>
                <p className="text-muted-foreground max-w-md mb-4">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'Create your first template to start streamlining project creation'}
                </p>
                {!searchTerm && (
                  <Button onClick={handleCreateTemplate}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="group border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold truncate">{template.name}</h4>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 shrink-0 -ml-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(template.id);
                            }}
                            aria-label={template.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <Star
                              className={cn(
                                'h-3.5 w-3.5',
                                template.isFavorite
                                  ? 'text-yellow-500 fill-current'
                                  : 'text-muted-foreground'
                              )}
                            />
                          </Button>
                        </div>
                        {template.templateDescription && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {template.templateDescription}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Template Details */}
                    <div className="space-y-1.5 text-xs text-muted-foreground mb-3">
                      {template.priority && (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {template.priority}
                          </Badge>
                          {template.type && (
                            <Badge variant="secondary" className="text-xs">
                              {template.type}
                            </Badge>
                          )}
                        </div>
                      )}

                      {template.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{template.location}</span>
                        </div>
                      )}

                      {template.clients && template.clients.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            {template.clients.length} client{template.clients.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}

                      {template.tags && template.tags.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Tag className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            {template.tags.length} tag{template.tags.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick(template)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Template Form Sheet */}
      <TemplateFormSheet
        open={formSheetOpen}
        onOpenChange={setFormSheetOpen}
        mode={sheetMode}
        template={selectedTemplate ? toFormTemplate(selectedTemplate) : undefined}
        onSave={handleSaveTemplate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
